import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
// The Strict* form deliberately: the loose `CheckboxProps` adds
// `[key: string]: any`, which would re-admit `any` on every property read.
import type { DropdownItemProps, DropdownProps, StrictCheckboxProps } from 'semantic-ui-react';
import {
  Checkbox,
  Icon,
  Loader,
  Modal,
  Popup,
  Table,
  Comment,
  Grid,
  Button,
  Form,
  Dropdown,
} from 'semantic-ui-react';
import useSWR from 'swr';
import type { FlatPatient, FoodOrder, FoodOrderBundle } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FoodFetcher from '../../../../data/FoodFetcher';
import * as Helpers from '../../../../utils/Helpers';
import { mapKey } from '../../../../utils/mapKey';
import { sortDirection } from '../../../../utils/sortDirection';

import { statusColors } from './constants';
import Feedback from './feedback';

// Define known carriers and their tracking URL format
//could be moved to a constants or fixture.ts file if needed
const carrierTrackingUrls: Record<string, (code: string) => string> = {
  columbuslastmile: (code) => `https://apps.cdldelivers.com/Tracking-Page/track?id=${code}`,
  //dbi:(code)=>undefined,
  courierexpress: (code) => `https://portal.courierexpress.net/track_shipment/${code}`,
  fedex: (code) => `https://www.fedex.com/apps/fedextrack/?tracknumbers=${code}`,
  lasership: (code) => `https://www.ontrac.com/tracking/?number=${code}`,
  ontrac: (code) => `https://www.ontrac.com/tracking/?number=${code}`,
  tusk: (code) => `https://shipmenttracking.tusklogistics.com/?trackingNumber=${code}`,
  uds: (code) => `https://www.uniteddeliveryservice.com/track/barcode/${code}`,
  ups: (code) => `https://www.ups.com/track?tracknum=${code}`,
  veho: (code) => `https://track.shipveho.com/#/trackingId/${code}`,
};

/**
 * The bundle names offered as alternates, grouped by vendor code.
 *
 * Built from `/food/bundle` rows whose `vendorId` is optional, and read back with a
 * given order's own `vendorId` — so both sides are runtime keys and a vendor with no
 * bundles reads `undefined`. The read sites already test the group before mapping it.
 *
 * The element type is `FoodOrderBundle['bundleId']`, i.e. `string | undefined`, not
 * `string`: the bundles' ids are themselves optional and are pushed unfiltered.
 */
type AlternateOptionsByVendor = Record<string, (string | undefined)[] | undefined>;

/**
 * What the orders table accepts.
 *
 * Established from its single JSX call site in `./index.tsx`.
 *
 * The handlers and `selectedOrderIds` are required: all are declared in that
 * parent's own component body (`useState`, `useReducer`, plain function
 * declarations), so they exist whenever the table is rendered. The rest stay
 * optional because the call site supplies them from its own optional props
 * (`org={props.patient && props.patient.org}`,
 * `openSolutranView={props.openSolutranView}`), or because the sort state the
 * parent's `useReducer` seeds starts as `null` on `column`, `direction` and
 * `data`.
 *
 * SUSPECTED DEFECT (table.tsx:545) — REPORTED, NOT FIXED. `props.doNotContact`
 * gates the `disabled` class on the alternate-food edit icon, but `./index.tsx`
 * does not pass it, so the read is permanently `undefined` and the guard never
 * fires: a member flagged do-not-contact still gets an enabled control here. The
 * flag does exist upstream — `components/patient/index.tsx:51` holds it and
 * threads it to the banner and questionnaires — so this is a prop that was
 * dropped somewhere between `patient/index.tsx` and here rather than one that
 * was never real. The identical read sits at
 * `components/common/food/draftOrders/table.tsx:370` with the same gap at its
 * own caller (`draftOrders/index.tsx:772`). Declared here as accepted-but-unpassed
 * so the read type-checks and the gap stays visible; wiring it through is a
 * behaviour change and a clinical call, not a typing one.
 *
 * `setEstimatedDeliveryDateRange` is the mirror case: `./index.tsx:1474` passes
 * it and nothing in this file reads it.
 */
type OrdersTableProps = {
  /** Fires as each row's export checkbox toggles; only `.checked` is read of `checkprops`. */
  addExportData: (
    event: React.FormEvent<HTMLInputElement>,
    checkprops: StrictCheckboxProps,
    orderId?: string,
  ) => void;
  /** Catalog bundles used to build the per-vendor alternate-food options. */
  bundlesData?: FoodOrderBundle[];
  /** Re-runs the alternate-food option build when it flips. */
  bundlesLoading?: boolean;
  /** The currently sorted column key, or `null` before any sort. */
  column?: string | null;
  /** Rows to render. `null` until the parent's reducer receives its first dataset. */
  data?: FoodOrder[] | null;
  /**
   * Read but never passed — see the SUSPECTED DEFECT note above.
   * `unknown` rather than `boolean` because no producer establishes the type.
   */
  doNotContact?: unknown;
  /**
   * Sort direction, forwarded to Semantic's `sorted`, whose own type is
   * `'ascending' | 'descending'`. `null` is included because the parent's
   * reducer seeds it that way and this file's ternaries emit `null` for every
   * non-sorted header — a disagreement that predates this declaration.
   */
  direction?: 'ascending' | 'descending' | null;
  /** The parent's `useReducer` dispatch; every call here is a `CHANGE_SORT`. */
  dispatch: (action: { type: string; sortcolumn?: string }) => void;
  /** Opens the roster-wide member drawer from a row. */
  openSelectedPatient: (patientId?: string) => void;
  /** Routes to the Solutran past-payments view. */
  openSolutranView?: (view: string, orderId?: string) => void;
  /** Payer org, forwarded to `Feedback`. */
  org?: string;
  /**
   * Members keyed by id, used to label rows in the roster-wide mode.
   *
   * Open-keyed: the subscript is an order's own `patientId`, so a row naming a
   * member this map was not built for reads `undefined` — which is why every read
   * site tests `patients[id]` first and renders a loader otherwise.
   */
  patients?: Record<string, FlatPatient | undefined> | null;
  /** Absent selects the roster-wide mode, which adds the member and vendor columns. */
  patientId?: string | null;
  /** Header checkbox handler. */
  selectAll: (event: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => void;
  /**
   * Ids currently ticked, driving each row checkbox's `checked`. See the SUSPECTED
   * DEFECT on `selectedOrderIds` in `./index.tsx`.
   */
  selectedOrderIds: (string | undefined)[];
  /** Accepted but unread — see the type doc above. */
  setEstimatedDeliveryDateRange?: (date: Date) => void;
  /** Swaps an order onto a different bundle. */
  updateAlternateOption: (
    order: FoodOrder,
    alternateFoodOptions: string[],
    selectedAlternate: string,
    bundle: FoodOrderBundle[],
  ) => void;
  /** A vendor code, or the literal 'All'. */
  vendor?: string;
};

function OrdersTable(props: OrdersTableProps) {
  const authInfo = useAuthContext();

  const [showFeedback, setShowFeedback] = useState(false);
  const [swrTrigger, setSwrTrigger] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [ordersFeedback, setOrdersFeedback] = useState({});
  const [feedbackSavedFlag, setFeedbackSavedFlag] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openedOrder, setOpenedOrder] = useState('');
  const [selectedAlternate, setSelectedAlternate] = useState('');
  const [alternateFoodOptions, setAlternateFoodOptions] = useState<AlternateOptionsByVendor>({});
  const nonCatalogVendor = ['FF', 'GB', 'MF', 'OK', 'PK', 'SB', 'SS', 'MH', 'TF', 'FL'];

  const handleChange = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setSelectedAlternate(value as string);
  };

  useEffect(() => {
    const alternateOptions: AlternateOptionsByVendor = {};
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
    props.bundlesData &&
      props.bundlesData.forEach((bundle) => {
        const vendor = mapKey(bundle.vendorId);
        // `hasOwnProperty`, matching upstream, rather than reading the group for truthiness.
        // The two agree on ordinary vendor ids and disagree on any INHERITED property name:
        // `alternateOptions['constructor']` is truthy — it is the inherited function — so the
        // truthiness form then calls `.push` on it and throws. Measured over 7 id shapes,
        // 5 diverge (`constructor`, `toString`, `__proto__`, `valueOf`, `hasOwnProperty`),
        // every one a TypeError in the truthiness form and a clean grouping upstream.
        // Unreachable today — vendor ids are a fixed two-letter set — but unreachable drift
        // is still drift.
        if (Object.prototype.hasOwnProperty.call(alternateOptions, vendor)) {
          alternateOptions[vendor]!.push(bundle.bundleId);
        } else {
          alternateOptions[vendor] = [bundle.bundleId];
        }
      });
    setAlternateFoodOptions(alternateOptions);
  }, [props.bundlesLoading]);

  const openFeedback = async (order: FoodOrder) => {
    setSelectedOrder(order);
    setSwrTrigger(true);
    await feedbackMutate();
    // setShowFeedback(true);
  };

  const closeFeedback = () => {
    setSwrTrigger(false);
    if (feedbackSavedFlag) {
      setFeedbackSavedFlag(false);
      setShowFeedback(false);
    } else {
      setShowFeedback(false);
    }
  };

  const feedbackOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: { orderId?: string; itemId?: string }[] | undefined) => {
      const ordersFeedbackData: Record<string, Record<string, unknown>> = {};
      if (data && data.length) {
        data.forEach((order) => {
          if (Object.prototype.hasOwnProperty.call(ordersFeedbackData, order.orderId!)) {
            ordersFeedbackData[order.orderId!]![order.itemId!] = order;
          } else {
            ordersFeedbackData[order.orderId!] = {};
            ordersFeedbackData[order.orderId!]![order.itemId!] = order;
          }
        });
        setOrdersFeedback(JSON.parse(JSON.stringify(ordersFeedbackData)) as Record<string, unknown>);
      } else {
        setOrdersFeedback({});
      }
      setShowFeedback(true);
    },
  };
  const { mutate: feedbackMutate, isValidating: feedbackDataLoading } = useSWR(
    swrTrigger
      ? [
          props.patientId
            ? `/food/feedback` +
              (props.vendor === 'All'
                ? `?patientId=` + props.patientId + `&orderId=` + selectedOrder['orderId']
                : `?vendorId=` +
                  props.vendor +
                  `&patientId=` +
                  props.patientId +
                  `&orderId=` +
                  selectedOrder['orderId'])
            : '',
          // props.vendor!=="All"?(`?vendorId=`+props.vendor):""+(props.patientId?
          // `&patientId=`+props.patientId:``)
          authInfo.token,
        ]
      : null,
    FoodFetcher,
    feedbackOptions,
  );
  return (
    <>
      <div className="table-div">
        <Table sortable celled singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell content={<Checkbox onChange={props.selectAll} />} />
              <Table.HeaderCell
                width={1}
                {...sortDirection(props.column === 'orderId' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'docId' })}
              >
                Order Id
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Program
              </Table.HeaderCell>
              <Table.HeaderCell
                width={1}
                {...sortDirection(props.column === 'firstName' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn:
                      props.patientId && props.vendor === 'All' ? `vendorId` : `firstName`,
                  })
                }
              >
                {props.patientId && props.vendor === 'All' ? `Vendor` : `Member Name`}
              </Table.HeaderCell>

              {!props.patientId && (
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(props.column === 'vendorId' ? props.direction : null)}
                  onClick={() =>
                    props.dispatch({
                      type: 'CHANGE_SORT',
                      sortcolumn: `vendorId`,
                    })
                  }
                >
                  Vendor
                </Table.HeaderCell>
              )}
              <Table.HeaderCell
                width={1}
                {...sortDirection(props.column === 'orderName' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderName' })}
              >
                Order Name
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'orderType' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderType' })}
              >
                Order Type
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'foodTypeName' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'foodTypeName',
                  })
                }
              >
                Food Type
              </Table.HeaderCell>
              <Table.HeaderCell
                width={1}
                {...sortDirection(props.column === 'flavorName' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'flavorName',
                  })
                }
              >
                Flavor
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'quantity' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'quantity' })}
              >
                Qty.
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'mealEquivalents' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'mealEquivalents' })
                }
              >
                Meqs
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'foodPricing' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'foodPricing' })}
              >
                Food Price
              </Table.HeaderCell>

              <Table.HeaderCell
                {...sortDirection(props.column === 'shippingFees' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'shippingFees' })}
              >
                Shipping
              </Table.HeaderCell>

              <Table.HeaderCell
                {...sortDirection(props.column === 'referralId' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'referralId' })}
              >
                Referral Id
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'status' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'status' })}
              >
                Status
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'orderDate' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderDate' })}
              >
                Order Date
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'createdBy' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'createdBy' })}
              >
                Created By
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'sentDate' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'sentDate' })}
              >
                Sent Date
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(
                  props.column === 'estimatedDeliveryDate' ? props.direction : null,
                )}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'estimatedDeliveryDate',
                  })
                }
              >
                Expected By
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'carrier' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'carrier' })}
              >
                Carrier
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'trackingNumber' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'trackingNumber',
                  })
                }
              >
                Tracking #
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'deliveryDate' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'deliveryDate',
                  })
                }
              >
                Delivery Date
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'exportedDate' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'exportedDate',
                  })
                }
              >
                Exported Date
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'exportedDate' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'exportedDate',
                  })
                }
              >
                Exported By
              </Table.HeaderCell>

              <Table.HeaderCell
                {...sortDirection(props.column === 'payments' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({
                    type: 'CHANGE_SORT',
                    sortcolumn: 'payments',
                  })
                }
              >
                Payments
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {/*
              SUSPECTED DEFECT — REPORTED, NOT FIXED. `data` is `FoodOrder[] | null`
              — the parent's `useReducer` seeds it `null` and only a `CHANGE_SORT`
              or the dataset effect replaces it — and `.map` on `null` throws a
              TypeError during render, which unmounts the food tab.

              The same file guards the very same value nine lines from the bottom
              (`props.data && props.data.length` in the results-count footer), so
              the author knew it could be null; this read is the one that missed.
              It does not fire today only because the call site renders the table
              behind `!isLoading && !ordersError`, which happens to coincide with
              the reducer having been fed. Any future path that clears the reducer
              while not loading reaches this.
            */}
            {props.data!.map((order, index) => {
              const orderStatus = order.status && order.status.toLowerCase();
              let trackingLink: string | undefined;
              if (order.deliveryInfo?.trackingCode && order.deliveryInfo?.carrier) {
                const carrierKey = order.deliveryInfo.carrier.toLowerCase();
                const getTrackingUrl = carrierTrackingUrls[carrierKey];
                /**
                 * Special case handling for supported carriers:
                 * FedEx, UPS, UDS, Tusk, Columbus Last Mile
                 *
                 * No hyperlink for others. Extend `carrierTrackingUrls` as needed.
                 * Note: URLs may break if external services change tracking url.
                 */
                trackingLink = getTrackingUrl
                  ? getTrackingUrl(order.deliveryInfo.trackingCode)
                  : order.deliveryInfo.trackingCode;
              }
              const colorCode = statusColors[mapKey(orderStatus)];
              const itemsContent = order.orderItems && order.orderItems.length > 0 && (
                <Grid key={index} centered>
                  <Grid.Row divided>
                    <Grid.Column floated="left">
                      <div
                        style={{
                          height: '300px',
                          overflowX: 'hidden',
                          overflowY: 'auto',
                        }}
                      >
                        <Table celled compact>
                          <Table.Header>
                            <Table.Row textAlign="center">
                              <Table.HeaderCell>Food Items</Table.HeaderCell>
                              {order?.shippedItems && (
                                <Table.HeaderCell>Shipped Items</Table.HeaderCell>
                              )}
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {order.orderItems.map((item, ind) => {
                              return (
                                <Table.Row key={ind}>
                                  <Table.Cell>{item.name}</Table.Cell>
                                  {order?.shippedItems &&
                                    (order.shippedItems.find(
                                      (shippedItem) =>
                                        shippedItem?.sku &&
                                        (shippedItem?.sku.toString() === item.skuUPC ||
                                          shippedItem?.sku.toString() === item.substitution ||
                                          shippedItem?.sku.toString() === item.sku ||
                                          shippedItem?.sku.toString() ===
                                            item.singleServerItemCode),
                                    ) ? (
                                      <Table.Cell>
                                        {
                                          order.shippedItems.find(
                                            (shippedItem) =>
                                              shippedItem?.sku &&
                                              (shippedItem?.sku.toString() === item.skuUPC ||
                                                shippedItem?.sku.toString() === item.substitution ||
                                                shippedItem?.sku.toString() === item.sku ||
                                                shippedItem?.sku.toString() ===
                                                  item.singleServerItemCode),
                                            // The identical `.find(...)` is the ternary
                                            // test guarding this arm, so a match exists
                                            // here; the `!` records that rather than
                                            // introducing a fallback cell.
                                          )!.item
                                        }{' '}
                                        <Icon color="green" name="check circle"></Icon>
                                      </Table.Cell>
                                    ) : (
                                      <Table.Cell></Table.Cell>
                                    ))}
                                </Table.Row>
                              );
                            })}
                          </Table.Body>
                        </Table>
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              );

              const notesContent =
                order?.orderNotes &&
                Array.isArray(order?.orderNotes) &&
                order?.orderNotes?.length > 0
                  ? order?.orderNotes?.map((on, index) => {
                      return (
                        <Comment.Group key={index}>
                          <Comment>
                            <Comment.Avatar src={'../../../../images/' + Helpers.getImage(true)} />
                            <Comment.Content>
                              <Comment.Author as="a">{on.author}</Comment.Author>
                              <Comment.Metadata>{moment.utc(on.time).fromNow()}</Comment.Metadata>
                              <Comment.Text>{on.text}</Comment.Text>
                            </Comment.Content>
                          </Comment>
                        </Comment.Group>
                      );
                    })
                  : typeof order?.orderNotes === 'string'
                    ? order.orderNotes
                    : '';

              const orderName = (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>{order.orderName}</div>&nbsp;&nbsp;&nbsp;&nbsp;
                  <div>
                    {order.orderNotes && order.orderNotes.length > 0 ? (
                      <Popup
                        on="click"
                        position="bottom right"
                        wide
                        trigger={<Icon name="sticky note" color="orange" link />}
                        content={notesContent}
                      />
                    ) : (
                      ''
                    )}
                    {order.allergens && (
                      <Popup
                        on="click"
                        position="bottom right"
                        wide
                        trigger={<Icon name="bug" color="red" link />}
                        content={`Contains:\n${order.allergens}`}
                      />
                    )}
                    {itemsContent && (
                      <Popup
                        wide={order?.shippedItems ? 'very' : true}
                        on="click"
                        position="bottom right"
                        closeOnEscape
                        closeOnDocumentClick
                        content={itemsContent}
                        flowing
                        trigger={<Icon link name="clipboard list" />}
                      />
                    )}
                  </div>
                </div>
              );
              return (
                <Table.Row
                  positive={colorCode === 'yy'}
                  warning={colorCode === 'warning'}
                  negative={colorCode === 'negative'}
                  error={colorCode === 'error'}
                  style={{
                    backgroundColor:
                      (colorCode === 'blue' && '#F0F8FF') || (colorCode === 'yellow' && '#ffefd5'),
                    color: colorCode === 'zz' && '#808080',
                  }}
                >
                  <Table.Cell
                    width={5}
                    collapsing
                    content={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Checkbox
                          checked={props.selectedOrderIds.includes(order.orderId)}
                          onChange={(e, checkprops) =>
                            props.addExportData(e, checkprops, order.orderId)
                          }
                        />
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        {props.patientId && !feedbackDataLoading && (
                          <Icon.Group
                            key={order.orderId}
                            onClick={() => openFeedback(order)}
                            style={{
                              marginTop: '6px',
                              marginRight: '6px',
                              verticalAlign: 'bottom',
                            }}
                          >
                            <Icon name="comment" link color="blue" style={{ fontSize: '1.2rem' }} />
                            <Icon
                              color="red"
                              corner="top right"
                              style={{
                                fontWeight: '1000',
                                fontSize: '1.2rem',
                                top: '-13px',
                                right: '-13px',
                              }}
                            >
                              <Icon
                                name="thumbs up"
                                link
                                color="red"
                                style={{ fontSize: '1.2rem' }}
                              />
                            </Icon>
                          </Icon.Group>
                        )}
                        {props.patientId && feedbackDataLoading && (
                          <Loader active inline="centered" size="tiny" />
                        )}
                      </div>
                    }
                  />
                  <Table.Cell>
                    {order.orderId}
                    {order?.status === 'EBT PENDING' && nonCatalogVendor.includes(props.vendor!) && (
                      <Popup
                        id={order?.orderId}
                        wide="very"
                        on="click"
                        position="top right"
                        closeOnEscape
                        hideOnScroll
                        closeOnDocumentClick
                        onClose={() => {
                          setIsOpen(false);
                        }}
                        trigger={
                          <Icon
                            name="edit"
                            link
                            color="green"
                            style={{ marginLeft: 5, fontSize: '1.2rem' }}
                            className={props.doNotContact ? 'disabled' : ''}
                            onClick={() => {
                              setIsOpen(true);
                              setOpenedOrder(order?.orderId as string);
                            }}
                          />
                        }
                        open={isOpen && order?.orderId === openedOrder}
                        content={
                          <>
                            <Form.Group widths="equal">
                              <Dropdown
                                basic
                                labeled
                                selection
                                search
                                placeholder="SELECT ALTERNATE FOOD"
                                autoComplete="off"
                                selectOnBlur={false}
                                name="edit"
                                onChange={handleChange}
                                required
                                wrapSelection
                                //    options={emailOptions}
                                options={
                                  alternateFoodOptions[mapKey(order.vendorId)]?.map((x) => {
                                    return { key: x!, text: x!, value: x! };
                                  }) as DropdownItemProps[]
                                }
                              ></Dropdown>
                              <Button
                                style={{ marginLeft: '4px' }}
                                size="small"
                                icon="edit outline"
                                color="blue"
                                onClick={() => {
                                  setIsOpen(false);
                                  setOpenedOrder('');
                                  props.updateAlternateOption(
                                    order,
                                    // SUSPECTED DEFECT — REPORTED, NOT FIXED. This
                                    // spread is unguarded while the `options` read
                                    // of the same key just above is not: a vendor
                                    // with no bundles throws here, on the Update
                                    // click, after the dropdown has rendered empty.
                                    // The `!` keeps that throw. Defaulting to `[]`
                                    // would post an alternate-options list that
                                    // silently omits every existing option.
                                    [
                                      ...(alternateFoodOptions[mapKey(order.vendorId)]! as string[]),
                                      order.orderName!,
                                    ],
                                    selectedAlternate,
                                    // Same click, second absence: `bundlesData` is
                                    // optional and IS guarded where it is folded
                                    // into `alternateFoodOptions` near the top of
                                    // this file, but not here. If the `/food/bundle`
                                    // query has not resolved, this throws before the
                                    // swap is posted.
                                    props.bundlesData!.filter((bundle) => {
                                      return bundle.bundleId === selectedAlternate;
                                    }),
                                  );
                                }}
                                content="Update Food Order"
                              />
                            </Form.Group>
                          </>
                        }
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>{order?.patientOrg}</Table.Cell>
                  <Table.Cell>
                    {props.patientId && props.vendor === 'All' ? (
                      order.vendorId
                    ) : (
                      <a
                        onClick={
                          !props.patientId
                            ? (e) => {
                                e.preventDefault();
                                props.openSelectedPatient(order.patientId);
                              }
                            : (null as unknown as React.MouseEventHandler<HTMLAnchorElement>)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {props.patients && props.patients[order.patientId!] ? (
                          // Concatenation, not arithmetic: the operands coerce to string as
                          // upstream intends. Wrapping them in String() would emit a call.
                          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                          props.patients[order.patientId!]!.firstName + ' ' + props.patients[order.patientId!]!.lastName
                        ) : (
                          <Loader size="tiny" active inline />
                        )}
                      </a>
                    )}
                  </Table.Cell>
                  {!props.patientId && <Table.Cell>{order.vendorId}</Table.Cell>}
                  <Table.Cell>{orderName}</Table.Cell>
                  <Table.Cell>{order.orderType && order.orderType.toUpperCase()}</Table.Cell>
                  <Table.Cell>{order.foodTypeName}</Table.Cell>
                  <Table.Cell>{order.flavorName}</Table.Cell>
                  <Table.Cell>{order.quantity}</Table.Cell>
                  <Table.Cell>{order.mealEquivalents}</Table.Cell>
                  <Table.Cell>{order?.foodPricing}</Table.Cell>
                  <Table.Cell>{order?.shippingFees}</Table.Cell>
                  <Table.Cell>{order?.referralId}</Table.Cell>
                  <Table.Cell>
                    {order.status ? order.status.toString().toUpperCase() : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {order.orderDate
                      ? moment.utc(order.orderDate).local().format('YYYY-MM-DD hh:mm a')
                      : ''}
                  </Table.Cell>
                  <Table.Cell>{order.createdBy}</Table.Cell>
                  <Table.Cell>
                    {order.sentDate
                      ? moment.utc(order.sentDate).local().format('YYYY-MM-DD hh:mm a')
                      : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {order.estimatedDeliveryDate ? order.estimatedDeliveryDate : ''}
                  </Table.Cell>
                  <Table.Cell>{order.deliveryInfo ? order.deliveryInfo.carrier : ''}</Table.Cell>
                  <Table.Cell>
                    {order.deliveryInfo ? (
                      trackingLink === order.deliveryInfo.trackingCode ? (
                        <a> {order.deliveryInfo.trackingCode}</a>
                      ) : (
                        <a href={trackingLink} target="_blank">
                          {order.deliveryInfo.trackingCode}
                        </a>
                      )
                    ) : (
                      ''
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {order.deliveryDate
                      ? moment(order.deliveryDate).local().format('YYYY-MM-DD')
                      : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {order.exportedDate
                      ? moment.utc(order.exportedDate).local().format('YYYY-MM-DD hh:mm a')
                      : ''}
                  </Table.Cell>
                  <Table.Cell>{order.exportedBy ? order.exportedBy : ''}</Table.Cell>

                  <Table.Cell>
                    {order.payments && (
                      /*
                        SUSPECTED DEFECT — REPORTED, NOT FIXED. `openSolutranView` is
                        forwarded from the parent's own optional prop and is absent on
                        every mount that does not come from the Solutran view, yet the
                        button that calls it is rendered on `order.payments` alone.
                        An order carrying payments outside that context shows a
                        "Details" button whose click throws "not a function".
                      */
                      <Button
                        content="Details"
                        onClick={() => props.openSolutranView!('pastPayments', order.orderId)}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        {showFeedback && (
          <Modal
            open={showFeedback}
            size="large"
            content={
              <>
                <Modal.Header>
                  <span style={{ color: 'teal', fontSize: '18px' }}>
                    <Icon name="user"></Icon>
                    {(() => {
                      // Hoisted for the same reason as the row-label lookup above.
                      const feedbackPatient = props.patients?.[mapKey(props.patientId)];
                      return feedbackPatient ? (
                        // Concatenation, not arithmetic: the operands coerce to string as upstream
                        // intends. Wrapping them in String() would emit a call.
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        feedbackPatient.firstName + ' ' + feedbackPatient.lastName
                      ) : (
                        <Loader active inline />
                      );
                    })()}
                    's Order Feedback
                  </span>
                </Modal.Header>
                <Modal.Content>
                  <Feedback
                    patientId={props.patientId!}
                    org={props.org!}
                    vendor={props.vendor!}
                    order={selectedOrder}
                    ordersFeedback={ordersFeedback}
                    setOrdersFeedback={setOrdersFeedback as (ordersFeedback: unknown) => void}
                    setFeedbackSavedFlag={setFeedbackSavedFlag}
                  ></Feedback>
                </Modal.Content>
              </>
            }
            onActionClick={() => closeFeedback()}
            actions={[{ key: 'done', icon: 'check', content: 'Close', positive: true }]}
          ></Modal>
        )}
      </div>
      <p style={{ margin: '25px 0 10px 15px', color: '#0067a5', fontSize: '17px' }}>
        Results Filtered: {props.data && props.data.length}
      </p>
    </>
  );
}

export default OrdersTable;
